// Client layer (TS-2.5.03) - UC-1's list screen. Composes the service hook (useProjects), the
// reference-data vocab for its filters, and ProjectsTable/ProjectFilters; contains no business
// logic of its own. "New project" is role-gated (Contributor+, BRD section 8.1's own line: "CRUD
// for Projects... (Contributor+)") client-side only - useCallerPermissions is UX, the Manager's
// IAuthorizationEngine call is the authoritative gate (RequireRole.tsx's own header makes the same
// point for route-level gating).
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ProjectFilters } from "./ProjectFilters";
import { ProjectsTable } from "./ProjectsTable";
import { useProjects } from "./hooks/useProjects";
import type { ProjectSortColumn, SortDirection } from "./lib/projectSort";
import { ALL_PAGE_SIZE, PAGE_SIZE_OPTIONS, readPageSizePreference, writePageSizePreference } from "./lib/pageSizePreference";
import type { PageSizeChoice } from "./lib/pageSizePreference";
import { useCallerPermissions } from "../../hooks/useCallerPermissions";
import { useReferenceList } from "../../hooks/useReferenceList";
import { PortfolioRoles } from "../../api/generated/contracts";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import type { ListProjectsParams } from "../../api/portfolioData";
import { readBooleanPreference, writeBooleanPreference } from "../../lib/booleanPreference";
import { strings } from "../../strings";
import "./projects.css";

const EDIT_ROLES = [PortfolioRoles.Contributor, PortfolioRoles.ProjectManager, PortfolioRoles.Admin];

// D-303: localStorage (not sessionStorage, unlike pageSizePreference.ts) - this remembers the
// viewer's hide-completed choice across browser restarts, separate from Data quality's own key.
const HIDE_COMPLETED_STORAGE_KEY = "eg.projects.hideCompleted";

export function ProjectsPage() {
  const { hasRole } = useCallerPermissions();

  const [nameContains, setNameContains] = useState("");
  const [teams, setTeams] = useState<readonly string[]>([]);
  const [statuses, setStatuses] = useState<readonly string[]>([]);
  const [sortColumn, setSortColumn] = useState<ProjectSortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ascending");
  const [pageNumber, setPageNumber] = useState(1);
  // D-259: 25 is the pre-existing default (unchanged for a viewer who has never touched the
  // selector this session); readPageSizePreference() only returns a value once this tab has made
  // an explicit choice.
  const [pageSize, setPageSize] = useState<PageSizeChoice>(() => readPageSizePreference() ?? 25);
  // D-303: default OFF, restored from this browser's remembered choice if one was made. A view
  // preference, not a typed filter - "Clear filters" (ProjectFilters.onClear below) never touches it.
  const [hideCompleted, setHideCompleted] = useState<boolean>(() => readBooleanPreference(HIDE_COMPLETED_STORAGE_KEY) ?? false);

  const params = useMemo<ListProjectsParams>(
    () => ({
      nameContains: nameContains.trim().length > 0 ? nameContains.trim() : undefined,
      teams: teams.length > 0 ? teams : undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      hideCompleted: hideCompleted ? true : undefined,
      pageNumber,
      pageSize,
    }),
    [nameContains, teams, statuses, hideCompleted, pageNumber, pageSize],
  );

  const { page, loading, error } = useProjects(params);
  const { list: teamList } = useReferenceList("team");
  const { list: statusList } = useReferenceList("project.status");

  function handleSort(column: ProjectSortColumn) {
    if (column === sortColumn) {
      setSortDirection((current) => (current === "ascending" ? "descending" : "ascending"));
    } else {
      setSortColumn(column);
      setSortDirection("ascending");
    }
  }

  function handlePageSizeChange(next: PageSizeChoice) {
    setPageSize(next);
    setPageNumber(1);
    writePageSizePreference(next);
  }

  function handleHideCompletedChange(next: boolean) {
    setHideCompleted(next);
    setPageNumber(1);
    writeBooleanPreference(HIDE_COMPLETED_STORAGE_KEY, next);
  }

  return (
    <section className="eg-projects">
      <header className="eg-projects__header">
        <h1>{strings.projects.heading}</h1>
        {hasRole(...EDIT_ROLES) && (
          <Link to="/projects/new" className="eg-button eg-button--primary">
            {strings.projects.newProject}
          </Link>
        )}
      </header>

      <div className="eg-projects__toolbar">
        <ProjectFilters
          nameContains={nameContains}
          onNameContainsChange={(next) => {
            setNameContains(next);
            setPageNumber(1);
          }}
          teams={teamList?.values ?? []}
          selectedTeams={teams}
          onSelectedTeamsChange={(next) => {
            setTeams(next);
            setPageNumber(1);
          }}
          statuses={statusList?.values ?? []}
          selectedStatuses={statuses}
          onSelectedStatusesChange={(next) => {
            setStatuses(next);
            setPageNumber(1);
          }}
          onClear={() => {
            setNameContains("");
            setTeams([]);
            setStatuses([]);
            setPageNumber(1);
            // D-303: hideCompleted is deliberately NOT reset here - it is a standing view
            // preference the viewer set on purpose (and persisted to localStorage), not a typed
            // filter "Clear filters" is meant to undo.
          }}
        />

        <div className="eg-projects__hide-completed">
          <label htmlFor="eg-project-hide-completed" title={strings.common.hideCompletedHint}>
            <input
              id="eg-project-hide-completed"
              type="checkbox"
              checked={hideCompleted}
              onChange={(event) => handleHideCompletedChange(event.target.checked)}
            />
            {strings.common.hideCompleted}
          </label>
        </div>

        <div className="eg-projects__page-size">
          <FormField id="eg-project-page-size" label={strings.projects.pagination.itemsToShow}>
            <select
              value={String(pageSize)}
              onChange={(event) => handlePageSizeChange(Number(event.target.value) as PageSizeChoice)}
              {...formFieldControlProps("eg-project-page-size")}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
              <option value={ALL_PAGE_SIZE}>{strings.projects.pagination.all}</option>
            </select>
          </FormField>
        </div>
      </div>

      <div className="eg-projects__table-card">
        {loading && !page && (
          <div role="status" aria-live="polite">
            {strings.projects.loading}
          </div>
        )}

        {error && (
          <div role="alert">
            <p>{strings.projects.errorTitle}</p>
            <p>{error.message}</p>
          </div>
        )}

        {page && page.items.length === 0 && <p>{strings.projects.emptyState}</p>}

        {page && page.items.length > 0 && (
          <>
            <ProjectsTable rows={page.items} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
            <nav className="eg-projects__pagination" aria-label={strings.projects.aria.pagination}>
              <button type="button" disabled={pageNumber <= 1} onClick={() => setPageNumber((current) => current - 1)}>
                {strings.projects.pagination.previous}
              </button>
              <span>
                Page {page.pageNumber} of {Math.max(1, Math.ceil(page.totalCount / page.pageSize))}
              </span>
              <button
                type="button"
                disabled={pageNumber * page.pageSize >= page.totalCount}
                onClick={() => setPageNumber((current) => current + 1)}
              >
                {strings.projects.pagination.next}
              </button>
            </nav>
          </>
        )}
      </div>
    </section>
  );
}
